import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { sendMsgToRoomUser, updateLocAlim } from 'services/api-service/center';
import { MomentService } from 'services/moment-service';
import { utilService } from 'services/util-service';
import { ActionCtor } from 'store/modules/main';
import { IStoreState } from '../../../store/modules';
import ListDatePicker from './ListDatePicker';
import ModalMessage from './ModalMessage';
import Table from './Table';

const AccidentIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 1.5C4.41 1.5 1.5 4.41 1.5 8C1.5 11.59 4.41 14.5 8 14.5C11.59 14.5 14.5 11.59 14.5 8C14.5 4.41 11.59 1.5 8 1.5ZM8 13C5.24 13 3 10.76 3 8C3 5.24 5.24 3 8 3C10.76 3 13 5.24 13 8C13 10.76 10.76 13 8 13Z"
      fill="#00E5FF"
    />
    <path
      d="M8 4.5C7.45 4.5 7 4.95 7 5.5V8.5C7 9.05 7.45 9.5 8 9.5C8.55 9.5 9 9.05 9 8.5V5.5C9 4.95 8.55 4.5 8 4.5Z"
      fill="#00E5FF"
    />
    <path
      d="M8 10.5C7.45 10.5 7 10.95 7 11.5C7 12.05 7.45 12.5 8 12.5C8.55 12.5 9 12.05 9 11.5C9 10.95 8.55 10.5 8 10.5Z"
      fill="#00E5FF"
    />
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.5 7C12.5 8.91 10.72 11.3 8.35 12.83C8.14 12.96 7.86 12.96 7.65 12.83C5.28 11.3 3.5 8.91 3.5 7C3.5 4.51 5.51 2.5 8 2.5C10.49 2.5 12.5 4.51 12.5 7Z"
      stroke="#8A99B0"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 8.5C8.82843 8.5 9.5 7.82843 9.5 7C9.5 6.17157 8.82843 5.5 8 5.5C7.17157 5.5 6.5 6.17157 6.5 7C6.5 7.82843 7.17157 8.5 8 8.5Z"
      stroke="#8A99B0"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11 13.5H5C3.34315 13.5 2 12.1569 2 10.5V10C2 9.44772 2.44772 9 3 9H13C13.5523 9 14 9.44772 14 10V10.5C14 12.1569 12.6569 13.5 11 13.5Z"
      stroke="#8A99B0"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CenterControlProps {
  onClick?: (roomId: string, isExpire: boolean) => void;
  onSetDate: (date: string) => void;
  onRegisterCase: (
    date: string,
    time: string,
    lat: string,
    lng: string,
    loc: string,
    dtl: string,
    rslt: string | undefined,
    dLoc: string,
    wCond: string,
    accType: string,
    dType: string,
    dRoute: string,
    mdn: string,
    alimType: string
  ) => Promise<boolean> | void;
}

const CenterList = ({ onClick, onSetDate, onRegisterCase }: CenterControlProps) => {
  const dispatch = useDispatch();
  const curRoom: any = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const roomInfos = useSelector((store: IStoreState) => store.MainReducer.roomInfos);
  const missingLocationAlerts = useSelector((store: IStoreState) => store.MainReducer.missingLocationAlerts);
  const [activeTab, setActiveTab] = useState<'accident' | 'alerts'>('accident');
  const [selectedDate, setSelectedDate] = useState(MomentService.getInstance().getDateFormat(new Date(), 'YYYYMMDD'));
  const [info, setInfo] = useState({
    show: false,
    roomId: '',
    type: '',
    count: 0,
  });

  const currentData = useMemo(() => {
    return activeTab === 'accident' ? roomInfos : missingLocationAlerts;
  }, [activeTab, roomInfos, missingLocationAlerts]);

  const fetchCurrentData = (tab: 'accident' | 'alerts', dateStr: string) => {
    const cleanDate = dateStr.replace(/-/g, '');
    if (tab === 'alerts') {
      dispatch(ActionCtor.getMissingLocationAlerts(cleanDate));
    } else {
      dispatch(ActionCtor.getRoomInfos(cleanDate));
    }
  };

  const handleTabChange = (tab: 'accident' | 'alerts') => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      fetchCurrentData(tab, selectedDate);
    }
  };

  const handleRescueClick = (row: any, columnType: 'reqCntR' | 'connectCntR') => {
    if (
      (columnType === 'reqCntR' && row.original.reqCntR > 0) ||
      (columnType === 'connectCntR' && row.original.connectCntR > 0)
    ) {
      setInfo((prev) => {
        return {
          ...prev,
          roomId: row.original.roomId,
          type: columnType,
          show: true,
          count: columnType === 'reqCntR' ? row.original.reqCntR : row.original.connectCntR,
        };
      });
    } else {
      toast('구조대원이 없습니다.', { type: 'error' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, row: any, columnType: 'reqCntR' | 'connectCntR') => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      handleRescueClick(row, columnType);
    }
  };

  const onCloseMessage = async (isSend: boolean, title: string, content: string) => {
    setInfo((prev) => {
      return {
        ...prev,
        roomId: '',
        type: '',
        show: false,
        count: 0,
      };
    });
    if (isSend) {
      try {
        const response: any = await sendMsgToRoomUser(
          info.roomId,
          info.type === 'connectCntR' ? 'C' : null,
          title,
          content
        );
        if (response.result === 'success') {
          toast('메시지를 전송하였습니다.', { type: 'success' });
        } else {
          toast(response.error_msg, { type: 'error' });
        }
      } catch (error) {
        console.log('sendMsgToRoomUser ', error);
      }
    }
  };

  const isExpired = (expire): string => {
    return expire.value ? '종료' : '진행';
  };

  const setGroup = (group): string => {
    return group.value === undefined ? '네모' : group.value;
  };

  const setTime = (time): string => {
    return time.value === undefined || time.value === '' ? '' : new Date(time.value).toTimeString().split(' ')[0];
  };

  const register = async (infos: any) => {
    try {
      const response: any = await updateLocAlim(
        infos.raiIdx ? String(infos.raiIdx) : undefined,
        infos.roomId || undefined,
        infos.vesselName || undefined,
        infos.captainName || undefined,
        infos.captainPhone || '',
        infos.lastSentTime || '',
        infos.lastLat ? String(infos.lastLat) : '',
        infos.lastLng ? String(infos.lastLng) : '',
        '사고등록',
        infos.alimType
      );
      if (response.result === 'success') {
        fetchCurrentData(activeTab, selectedDate);
        const now = new Date();
        const currentDate = now.toISOString().split('T')[0];
        const currentTime = now.toTimeString().split(' ')[0];

        const formattedLastReceivedTime = infos.lastSentTime
          ? dayjs(infos.lastSentTime).format('YYYY-MM-DD HH:mm:ss')
          : '-';

        const description = [
          `선박명 : ${infos.vesselName || infos.shipName || '-'}`,
          `선장이름(전화번호) : ${infos.captainName || '-'}(${infos.captainPhone || ''})`,
          `마지막 위치수신 시간 : ${formattedLastReceivedTime}`,
        ].join('\n');

        onRegisterCase(
          currentDate,
          currentTime,
          infos.lastLat,
          infos.lastLng,
          '확인중',
          description,
          undefined,
          '확인중',
          '확인중',
          '확인중',
          '실종자 수색',
          '해양경찰',
          infos.captainPhone,
          infos.alimType
        );

        dispatch(
          ActionCtor.setPassenger([
            {
              phone: infos.captainPhone,
              name: infos.captainName,
            },
          ])
        );

        handleTabChange('accident');
      } else {
        toast('사고등록에 실패하였습니다.', { type: 'error' });
      }
    } catch (error) {
      console.error('사고 등록 중 오류 발생:', error);
    }
  };

  const pass = async (infos: any) => {
    try {
      const response: any = await updateLocAlim(
        infos.raiIdx ? String(infos.raiIdx) : undefined,
        infos.roomId || undefined,
        infos.vesselName || undefined,
        infos.captainName || undefined,
        infos.captainPhone || '',
        infos.lastSentTime || '',
        infos.lastLat ? String(infos.lastLat) : '',
        infos.lastLng ? String(infos.lastLng) : '',
        '미처리',
        infos.alimType
      );
      if (response.result === 'success') {
        fetchCurrentData(activeTab, selectedDate);
      } else {
        toast('미처리에 실패하였습니다.', { type: 'error' });
      }
    } catch (error) {
      console.error('', error);
    }
  };

  const accidentColumns = useMemo(
    () => [
      {
        Header: '접수',
        columns: [
          { accessor: 'isExpire', Header: '상태', Cell: (props: any) => isExpired(props) },
          { accessor: 'belong', Header: '소속', Cell: (props: any) => setGroup(props) },
          { accessor: 'userName', Header: '접수자' },
          { accessor: 'saveTime', Header: '시작', Cell: (props: any) => setTime(props) },
          { accessor: 'expireTime', Header: '종료', Cell: (props: any) => setTime(props) },
        ],
      },
      {
        Header: '구조대',
        columns: [
          {
            accessor: 'reqCntR',
            Header: '요청',
            Cell: (props: any) => (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRescueClick(props.row, 'reqCntR');
                }}
                onKeyDown={(e) => handleKeyDown(e, props.row, 'reqCntR')}
                style={{ cursor: 'pointer' }}
                aria-label={`구조대 요청 ${props.value}건`}
              >
                {props.value}
              </div>
            ),
          },
          {
            accessor: 'connectCntR',
            Header: '연결',
            Cell: (props: any) => (
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRescueClick(props.row, 'connectCntR');
                }}
                onKeyDown={(e) => handleKeyDown(e, props.row, 'connectCntR')}
                style={{ cursor: 'pointer' }}
                aria-label={`구조대 연결 ${props.value}건`}
              >
                {props.value}
              </div>
            ),
          },
        ],
      },
      {
        Header: '요구조자',
        columns: [
          {
            accessor: 'reqCntV',
            Header: '요청',
          },
          {
            accessor: 'connectCntV',
            Header: '연결',
          },
        ],
      },
    ],
    [activeTab]
  );

  const alertsColumns = useMemo(
    () => [
      {
        accessor: 'alertTime',
        Header: '알림시간',
        width: 100,
        Cell: (props: any) => {
          const fullDateTime = props.row.original.saveTime || props.value;
          const timeOnly = fullDateTime ? fullDateTime.split(' ')[1] : '-';
          return <div>{timeOnly}</div>;
        },
      },
      {
        accessor: 'shipInfo', // 혹은 실제 필드명 'shipName'
        Header: '선박정보',
        width: 120,
        Cell: (props: any) => <div>{props.row.original.vesselName || props.value}</div>,
      },
      {
        accessor: 'captainInfo',
        Header: '선장정보',
        width: 110,
        Cell: (props: any) => (
          <div style={{ lineHeight: '1.4' }}>
            <div>{props.row.original.captainName || '-'}</div>
            <div style={{ color: '#8A99B0', fontSize: '13px' }}>
              ({utilService.formatPhoneNumber(props.row.original.captainPhone ?? '') || '-'})
            </div>
          </div>
        ),
      },
      {
        accessor: 'locationInfo',
        Header: '위치정보',
        width: 200,
        Cell: (props: any) => {
          const lastSentTime = props.row.original.lastSentTime;
          const timeOnly = lastSentTime ? lastSentTime.split(' ')[1] : '-';
          return (
            <div style={{ lineHeight: '1.4' }}>
              <div>
                {utilService.convertToDms(props.row.original.lastLat, false)},
                {utilService.convertToDms(props.row.original.lastLng, true)}
              </div>
              <div style={{ color: '#8A99B0', fontSize: '13px' }}>({timeOnly})</div>
            </div>
          );
        },
      },
      {
        accessor: 'status',
        Header: '사고등록 상태',
        width: 130,
        Cell: (props: any) => {
          const status = props.row.original.status;
          const registerTime = props.row.original.updtTime;

          return (
            <StatusButtonWrap>
              {!status ? (
                <>
                  <ActionButton onClick={() => register(props.row.original)}>사고등록</ActionButton>
                  <NotActionButton onClick={() => pass(props.row.original)}>미처리</NotActionButton>
                </>
              ) : (
                <div style={{ lineHeight: '1.4' }}>
                  <StatusText>{status}</StatusText>
                  {registerTime && <StatusTime>{registerTime}</StatusTime>}
                </div>
              )}
            </StatusButtonWrap>
          );
        },
      },
    ],
    [activeTab]
  );

  const columns = activeTab === 'accident' ? accidentColumns : alertsColumns;

  const onChangeDate = (value: string) => {
    onSetDate(value.replace(/-/g, ''));
    setSelectedDate(value);
    fetchCurrentData(activeTab, value);
  };

  useEffect(() => {
    onSetDate(selectedDate.replace(/-/g, ''));
  }, []);

  useEffect(() => {
    if (curRoom !== undefined) {
      const newDate = MomentService.getInstance().getDateFormat(new Date(curRoom.saveTime), 'YYYYMMDD');
      setSelectedDate(newDate);
      onSetDate(newDate);
    }
  }, [curRoom]);

  return (
    <CenterListTemplateWrap>
      <HeaderControls>
        <TabGroup>
          <TabButton active={activeTab === 'accident'} onClick={() => handleTabChange('accident')}>
            <AccidentIcon />
            해양사고 접수 목록
          </TabButton>
          <TabButton active={activeTab === 'alerts'} onClick={() => handleTabChange('alerts')}>
            <LocationIcon />
            위치소실 알림 목록
          </TabButton>
        </TabGroup>
        <ListDatePicker value={selectedDate} onChange={onChangeDate}></ListDatePicker>
      </HeaderControls>

      <HorizontalWrap>
        <VerticalWrap>
          <TableWrap>
            {currentData.length === 0 ? (
              <EmptyText>데이터가 없습니다.</EmptyText>
            ) : (
              <Table
                key={activeTab}
                columns={columns}
                data={currentData}
                onClick={onClick}
                isActiveAlerts={activeTab === 'alerts'}
              />
            )}
          </TableWrap>
        </VerticalWrap>
      </HorizontalWrap>
      {info.show && <ModalMessage info={info} onClose={onCloseMessage} />}
    </CenterListTemplateWrap>
  );
};

const CenterListTemplateWrap = styled.div`
  width: 100%;
  height: calc(100% - 43.44rem);
`;
const HeaderControls = styled.div`
  display: flex;
  height: 72px;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(
    90deg,
    #001e3a 0%,
    #00203d 10%,
    #00223f 20%,
    #002442 30%,
    #002644 40%,
    #002847 50%,
    #002a4a 60%,
    #002c4d 70%,
    #002d50 80%,
    #002f53 90%,
    #003156 100%
  );
`;
const TabGroup = styled.div`
  display: flex;
  background: #001224;
  border-radius: 16px;
  padding: 6px;
  gap: 6px;
  width: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin-left: 20px;
`;
const TabButton = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 9px 22px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 15px;
  font-weight: 600;

  border: ${(props) => (props.active ? '0.57px solid #00b8db4d' : 'none')};
  background: ${(props) => (props.active ? '#1A3A5A' : 'linear-gradient(167.82deg, #314158 6.96%, #1D293D 93.04%)')};
  color: ${(props) => (props.active ? '#cefafe' : '#4a5b75')};
  svg path,
  svg rect {
    fill: ${(props) => (props.active ? '#00E5FF' : '#4a5b75')};
  }
`;
const HorizontalWrap = styled.div`
  width: 100%;
  height: 100%;
  max-width: 47.5rem;
  display: flex;
`;
const VerticalWrap = styled.div`
  width: 100%;
  background: #001e3a;
`;
const TableWrap = styled.div`
  height: calc(100% - 4rem);
  border: 1px solid #000000;
  background: linear-gradient(180deg, #002f5a 0%, #000 100%);
  overflow-y: auto;
`;
const EmptyText = styled.h1`
  margin-top: 2.5rem;
  font-weight: 500;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: #ffffff;
  text-align: center;
`;
const StatusButtonWrap = styled.div`
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
`;
const ActionButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(167.82deg, rgba(0, 184, 219, 0.4) 6.96%, rgba(0, 100, 150, 0.5) 93.04%);
  color: #ffffff;
  border: 0.57px solid #00d3f366;

  &:hover {
    filter: brightness(1.2);
  }
`;
const NotActionButton = styled(ActionButton)`
  background: linear-gradient(180deg, #0a3385 0%, #031b4c 100%);
  border: 1px solid #ffffff33;
`;
const StatusText = styled.div`
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;
const StatusTime = styled.div`
  font-size: 12px;
  color: #8a99b0;
  margin-top: 2px;
`;

export default CenterList;
